import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Select, 
    Option,
    Radio,
    Card,
    List,
    ListItem,
    ListItemPrefix,
    Typography,
    Alert
} from "@material-tailwind/react";
import ScheduleList from './ScheduleList';

function Icon() {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
        />
      </svg>
    );
}

type Competition = {
    comp_code: string;   // DB에서 문자열 형태라면 string
    comp_date: string;   // 날짜가 "2024-06-23" 혹은 "2024" 형태
    comp_title: string;
    comp_div_ls: string[]; // DB에서 "10km,20km"처럼 문자열로 넘어옴
    comp_place: string | null;
    comp_link: string | null;
};

const ScheduleDate: React.FC = () => {
    // 1) 원본 데이터를 저장할 state
    const [dates, setDates] = useState<Competition[]>([]);
    
    // 2) 필터링된 데이터를 저장할 state
    const [filteredDates, setFilteredDates] = useState<Competition[]>([]);

    // 3) 날짜/연도/월 선택 state
    const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
    const currentYear = new Date().getFullYear().toString();
    const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(`${parseInt(currentMonth)}월`);

    // 4) 알림 및 라디오 버튼 비활성화 관련 state
    const [showAlert, setShowAlert] = useState(false);
    const [disableRadio, setDisableRadio] = useState(false);

    // --- 백엔드에서 데이터 호출 ---
    useEffect(() => {
        axios.get('/api/competitions')
            .then((response) => {
                // DB에서 넘어온 데이터(response.data)는 comp_div_ls가 "10km,20km" 등 문자열이므로
                // 기존 컴포넌트에서 사용하는 형식에 맞게 가공해줘야 함
                // (예: comp_div_ls를 배열로 바꾼다거나, comp_date를 다른 필드명으로 변경 등)
                const fetchedData: Competition[] = response.data;
                
                // 필요하다면 comp_date(날짜) 파싱/가공 로직 추가
                // 예: fetchedData.map(...) 해서 comp_div_ls를 나눠서 배열로 저장하거나, comp_date → comp_date 등
                
                setDates(fetchedData);
            })
            .catch((error) => {
                console.error("Error fetching competitions:", error);
            });
    }, []);

    // --- 연도/월 선택 변경 시 필터링 ---
    useEffect(() => {
        let filtered = [...dates]; // 원본 복사

        // 1) 연도 필터
        if (selectedYear) {
            // DB에서 comp_date가 "YYYY-MM-DD" 형태라면 substring(0,4) 등으로 연도만 추출
            // 만약 "2024"만 저장되어 있을 수도 있으니, 그 상황도 같이 고려
            filtered = filtered.filter(dateObj => {
                // 예: "2024-06-23" → dateObj.comp_date.substring(0, 4) === "2024"
                //     "2024"        → 동일하게 substring(0,4) 가능
                const yearString = dateObj.comp_date.substring(0, 4);
                return yearString === selectedYear;
            });
        }

        // 2) 월 필터
        if (selectedMonth) {
            const monthIndex = months.indexOf(selectedMonth) + 1;
            const monthString = monthIndex.toString().padStart(2, '0'); // "01", "02"...
            // 예: dateObj.comp_date가 "YYYY-MM-DD"인 경우 monthString( "06" ) 비교
            // 혹은 "YYYY-MM" 형태일 경우 substring(5,7)로 월 비교
            // 만약 "YYYY"만 있는 데이터(월 정보가 없음)인 경우, 조건을 통과하지 못할 수도 있음
            filtered = filtered.filter(dateObj => {
                // 예: "2024-06-23" → dateObj.comp_date.substring(5,7) === "06"
                if (dateObj.comp_date.length >= 7) {
                    const dataMonth = dateObj.comp_date.substring(5, 7);
                    return dataMonth === monthString;
                }
                return false; // 연도만 있는 경우나 잘못된 데이터 형식이면 제외
            });
        }

        // 3) 날짜 내림차순 정렬 (필요 시)
        filtered.sort((a, b) => {
            // "2024-06-23" 같은 형태면 new Date()로 변환 가능
            // "2024"만 있으면 Date 변환이 애매할 수 있음
            // 여기서는 단순히 문자열로 비교하거나, 혹은 길이에 따라 파싱 로직 다르게
            const dateA = new Date(a.comp_date);
            const dateB = new Date(b.comp_date);
            return dateB.getTime() - dateA.getTime();
        });

        setFilteredDates(filtered);
    }, [selectedYear, selectedMonth, dates]);

    // --- 연도/월 선택 핸들러 ---
    const handleYearChange = (value: string | undefined) => {
        setSelectedYear(value || '');
    };

    const handleMonthChange = (month: string) => {
        if (!selectedYear) {
            setShowAlert(true);
            setDisableRadio(true);
            setTimeout(() => {
                setShowAlert(false);
                setDisableRadio(false);
            }, 1500);
            return;
        }
        setSelectedMonth(month);
    };

    const handleMonthSelectChange = (value: string | undefined) => {
        handleMonthChange(value || '');
    };

    return (
        <div>
            <div className='flex justify-center '>
                <div className='flex items-center gap-4 mt-3 lst:flex-col'>
                    {/* --- 연도 선택 --- */}
                    <div className='w-25'>
                        <Select size='lg' label="연도" value={selectedYear} onChange={(e) => handleYearChange(e)}>
                            {/* dates에서 추출한 연도 목록을 사용해도 됨 */}
                            {/* 혹은 2020~2030 고정 */}
                            {[...new Set(dates.map(d => d.comp_date.substring(0, 4)))]
                                .filter(year => year.trim() !== '')
                                .sort()
                                .map((year, index) => (
                                    <Option key={index} value={year}>{year}년</Option>
                                ))
                            }
                        </Select>
                    </div>
                    {/* --- (모바일에서) 월 선택 Select 박스 --- */}
                    <div className='w-25 dt:hidden'>
                        <Select size='lg' label="월" value={selectedMonth} onChange={(e) => handleMonthSelectChange(e)}>
                            {months.map((month, index) => (
                                <Option key={index} value={month}>{month}</Option>
                            ))}
                        </Select>
                    </div>
                    {/* --- (데스크톱에서) 월 선택 Radio --- */}
                    <Card className="border-blue-gray-400 border-2 w-[73rem] ph:hidden">
                        <List className="flex-row">
                            {months.map((month, index) => (
                                <ListItem key={index} className="p-0">
                                    <label
                                        htmlFor={`horizontal-list-${month}`}
                                        className="flex w-full cursor-pointer items-center px-3 py-2"
                                    >
                                        <ListItemPrefix className="mr-3">
                                            <Radio
                                                name="horizontal-list"
                                                id={`horizontal-list-${month}`}
                                                ripple={false}
                                                className="hover:before:opacity-0"
                                                containerProps={{
                                                    className: "p-0",
                                                }}
                                                checked={selectedMonth === month}
                                                onChange={() => handleMonthChange(month)}
                                                disabled={disableRadio}
                                            />
                                        </ListItemPrefix>
                                        <Typography
                                            color="blue-gray"
                                            className="font-medium text-blue-gray-400"
                                        >
                                            {month}
                                        </Typography>
                                    </label>
                                </ListItem>
                            ))}
                        </List>
                    </Card>
                </div>
            </div>

            {/* --- 연도 미선택 시 경고 알림 --- */}
            {showAlert && (
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <Alert icon={<Icon />} className='w-80 bg-blue-gray-400'>
                        연도를 먼저 선택해주세요
                    </Alert>
                </div>
            )}

            {/* --- 필터링된 리스트 출력 --- */}
            <div className='flex justify-center'>
                {/* ScheduleList는 기존 그대로 활용하되,
                    props로 넘기는 dates에 comp_div_ls(문자열)를
                    배열로 변환해 넘기도록 처리할 수도 있음 */}
                <ScheduleList
                  dates={filteredDates.map(competition => ({
                    // ScheduleList에서 사용하는 필드에 맞춰 변환
                    // comp_date → comp_date
                    comp_date: competition.comp_date,
                    comp_code: competition.comp_code, // string → number
                    comp_title: competition.comp_title,
                    // 문자열을 ',' 기준으로 나눠 배열화
                    comp_div_ls: competition.comp_div_ls
                      ? competition.comp_div_ls
                      : [],
                    // DB가 null일 수도 있으니 기본값 처리
                    comp_place: competition.comp_place || '',
                    comp_link: competition.comp_link || ''
                  }))}
                />
            </div>
        </div>
    );
};

export default ScheduleDate;
